// Helpers visuales compartidos de la pantalla de planta (P1).
//
// La TV se lee a 3+ metros: tipografías generosas, tabular numbers en TODA
// cifra, color solo con significado (semáforo unificado 90/70 de la
// plataforma; el rojo <60 dispara además el andon).

import type { ReactNode } from 'react';

/**
 * Etiquetas del marco y de los paneles (encabezado, pie, rótulos). Escalan con
 * el ancho de la pantalla: 14 px a 1280 de ancho, hasta 20 px en TV grande.
 * La variable `--pantalla-etiqueta` la define el lienzo de PantallaPage.
 */
export const ETIQUETA = 'text-[length:var(--pantalla-etiqueta,0.875rem)]';

export const fmt = new Intl.NumberFormat('es-CO', { maximumFractionDigits: 0 });

const fmtFechaLarga = new Intl.DateTimeFormat('es-CO', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
});

/**
 * «Lunes, 12 de octubre»: solo la primera letra en mayúscula. La clase CSS
 * `capitalize` ponía mayúscula en cada palabra («12 De Octubre»).
 */
export function fechaLargaPantalla(fecha: Date): string {
  const texto = fmtFechaLarga.format(fecha);
  return texto.charAt(0).toLocaleUpperCase('es-CO') + texto.slice(1);
}

/** Nombre para TV: primer nombre + primer apellido (pedido UPG 2026-08-25). */
export function nombreCorto(nombre: string, apellido: string): string {
  return [nombre.trim().split(/\s+/)[0], apellido.trim().split(/\s+/)[0]].filter(Boolean).join(' ');
}

/** Color semántico por % de cumplimiento (tokens del design system). */
export function colorPct(pct: number | null): string {
  if (pct === null) return 'var(--fg-tertiary)';
  if (pct >= 90) return 'var(--ok)';
  if (pct >= 70) return 'var(--warn)';
  return 'var(--crit)';
}

/** Barra de progreso horizontal, color por semáforo (o fijo). */
export function Barra({
  pct,
  color,
  height = 10,
}: {
  pct: number | null;
  color?: string;
  height?: number;
}) {
  const ancho = pct === null ? 0 : Math.min(100, Math.max(0, pct));
  return (
    <div
      className="w-full rounded-full bg-surface-overlay"
      style={{ height }}
      role="progressbar"
      aria-valuenow={Math.round(ancho)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        className="h-full rounded-full transition-[width] duration-700 ease-out"
        style={{ width: `${ancho}%`, background: color ?? colorPct(pct) }}
      />
    </div>
  );
}

/** Título editorial del panel: serif de marca (excepción editorial del branding §4). */
export function TituloPanel({ titulo, subtitulo }: { titulo: string; subtitulo: string }) {
  return (
    <div className="flex items-end justify-between gap-6">
      <div>
        <h1 className="font-serif text-[clamp(2.2rem,3.4vw,3.4rem)] leading-none text-fg">
          {titulo}
        </h1>
        <p className="mt-2 text-[clamp(0.9rem,1.1vw,1.15rem)] text-fg-secondary">{subtitulo}</p>
      </div>
      <EnVivo />
    </div>
  );
}

/** Indicador "EN VIVO" con punto pulsante. */
export function EnVivo() {
  return (
    <div className="flex items-center gap-2 pb-1">
      <span className="relative flex h-2.5 w-2.5" aria-hidden="true">
        <span
          className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-60"
          style={{ background: 'var(--ok)' }}
        />
        <span
          className="relative inline-flex h-2.5 w-2.5 rounded-full"
          style={{ background: 'var(--ok)' }}
        />
      </span>
      <span className={`${ETIQUETA} tracking-[0.14em] text-fg-secondary`}>EN VIVO</span>
    </div>
  );
}

/** Corona del primer puesto (SVG inline, outline sobrio). */
export function Corona({ size = 22 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 17h18M4 17 3 7l5 4 4-6 4 6 5-4-1 10" />
    </svg>
  );
}

/** Estado vacío de un panel (sin datos en el turno). */
export function PanelVacio({ mensaje, children }: { mensaje: string; children?: ReactNode }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center">
      <p className="font-serif text-[clamp(1.6rem,2.2vw,2.4rem)] text-fg-tertiary">{mensaje}</p>
      {children}
    </div>
  );
}
